import type { Trail } from "@hash/shared";

// Trail `date` is a wall-clock calendar date, "YYYY-MM-DD", with no time. The
// naive `new Date("2026-06-02")` parses it as UTC midnight, so a browser in a
// negative-UTC zone renders the previous calendar day — the trail looks a day
// early. We never want that shift: a trail dated June 2 reads "Jun 2" for every
// viewer. Building the Date from the Y/M/D parts (local midnight) keeps the day
// component intact in any browser, with no UTC round-trip.

const DEFAULT_OPTS: Intl.DateTimeFormatOptions = {
  weekday: "short",
  month: "short",
  day: "numeric",
  year: "numeric",
};

/**
 * The viewer's local calendar date shifted by `offsetDays`, as "YYYY-MM-DD" —
 * same wall-clock semantics as `Trail.date`, so the two compare lexically.
 */
export function localDate(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Render a "YYYY-MM-DD" calendar date as authored, never shifted by the viewer's
 * zone. Accepts a full ISO datetime too (legacy `lastAttendedAt` values) and
 * uses just its date portion. Same wall-clock semantics as `Trail.date`.
 */
export function formatCalendarDate(
  value: string,
  opts: Intl.DateTimeFormatOptions = DEFAULT_OPTS
): string {
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (![y, m, d].every(Number.isFinite)) return value;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, opts);
}

/** Render a trail's calendar date as authored, never shifted by the viewer's zone. */
export function formatTrailDate(
  trail: Pick<Trail, "date">,
  opts: Intl.DateTimeFormatOptions = DEFAULT_OPTS
): string {
  return formatCalendarDate(trail.date, opts);
}

/**
 * The trail's *local* start time-of-day (e.g. "6:30 PM"), taken from the offset
 * embedded in `startAt` — not converted to the viewer's zone. Returns "" when
 * the trail is date-only. We read the wall-clock straight off the ISO string
 * rather than via Date so the viewer's own zone can never shift it.
 */
export function formatTrailTime(trail: Pick<Trail, "startAt">): string {
  const t = wallClock(trail.startAt);
  if (!t) return "";
  const period = t.hh < 12 ? "AM" : "PM";
  const h12 = t.hh % 12 === 0 ? 12 : t.hh % 12;
  return `${h12}:${String(t.mm).padStart(2, "0")} ${period}`;
}

/**
 * Compact single-line date + time, e.g. "Wed Jun 17 - 7PM" ("6:30PM" off the
 * hour; just "Wed Jun 17" for a date-only trail). Same zone-safety rules as
 * the formatters above: the authored day and the trail's own wall-clock.
 */
export function formatTrailDateTimeShort(
  trail: Pick<Trail, "date" | "startAt">
): string {
  const [y, m, d] = trail.date.split("-").map(Number);
  let date = trail.date;
  if ([y, m, d].every(Number.isFinite)) {
    const dt = new Date(y, m - 1, d);
    // Two formatter calls so there's no locale comma ("Wed, Jun 17").
    const weekday = dt.toLocaleDateString(undefined, { weekday: "short" });
    const monthDay = dt.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
    date = `${weekday} ${monthDay}`;
  }
  const t = wallClock(trail.startAt);
  if (!t) return date;
  const period = t.hh < 12 ? "AM" : "PM";
  const h12 = t.hh % 12 === 0 ? 12 : t.hh % 12;
  return `${date} - ${h12}${t.mm ? `:${String(t.mm).padStart(2, "0")}` : ""}${period}`;
}

/**
 * Whole days between two "YYYY-MM-DD" calendar dates (`b` minus `a`). Both
 * sides are parsed as UTC midnight purely so the subtraction lands on exact
 * day boundaries — this never touches the viewer's zone, since a date-only
 * string carries no time-of-day to shift.
 */
export function daysBetweenDates(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round(
    (Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000
  );
}

/** The wall-clock hour/minute embedded in startAt, or null when date-only. */
function wallClock(startAt?: string): { hh: number; mm: number } | null {
  const match = startAt?.match(/T(\d{2}):(\d{2})/);
  if (!match) return null;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return { hh, mm };
}
